import { lccstate } from 'lcc-state';

import { i18n } from '@dojo/framework/core/middleware/i18n';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { LocalizedMessages } from '@dojo/framework/core/mixins/I18n';
import { create, v } from '@dojo/framework/core/vdom';
import { Bundle, Messages } from '@dojo/framework/i18n/i18n';
import { asMoment } from '@holmescorp/dates';

import { css } from '../css/announcements.m.css';
import { Announcements as AnnouncementRecords, svc } from '../support/svc';

interface AnnouncementsICache {
	records: AnnouncementRecords[];
}

const factory = create({
	icache: createICacheMiddleware<AnnouncementsICache>(),
	lccstate,
	theme,
	i18n
});

export const Announcements = factory(({ middleware: { icache, lccstate, theme, i18n } }) => {
	const nls = (): LocalizedMessages<Messages> => {
		const { get, path } = lccstate;
		const dynls = get(path('dynls'));
		const bundle: Bundle<Messages> | undefined = dynls && dynls.get('settings');
		if (!bundle) {
			return { isPlaceholder: true, format: () => '', messages: {} as Messages };
		}
		return i18n.localize(bundle);
	};

	const { isPlaceholder, messages } = nls();
	if (isPlaceholder) {
		return null;
	}

	const { noAnnouncements = '' } = messages;

	const themedCss = theme.classes(css);

	const { get, path } = lccstate;
	const sitestatus = get(path('sitestatus'));

	const all_announcements = icache.getOrSet('records', async () => {
		const start = 0;
		const count = 100;
		const end = start + count;
		return await svc.announcements({
			session_id: window.hcstatic.course_session ? window.hcstatic.course_session.pk : undefined,
			select: {
				dstore: true,
				start,
				count,
				end,
				filter: {
					act: 'eq',
					args: ['published', true]
				}
			}
		});
	});

	const record_rows = all_announcements
		? all_announcements.map((record) => {
				const pubdate = asMoment(record.pubdate);
				const pubdate_formatted = pubdate.format('lll');
				const panel_styles = [themedCss.panel, themedCss.panelDefault];

				if (sitestatus.prior_login) {
					const prior = asMoment(sitestatus.prior_login);

					if (pubdate.isAfter(prior)) {
						panel_styles[1] = themedCss.panelWarning;
					}
				}

				return v('div', { classes: panel_styles, xqa: 'announcement-panel' }, [
					v('div', { classes: [themedCss.panelHeading] }, [
						v('h3', { classes: [themedCss.announcementTitle], innerHTML: record.title }),
						v('h4', { classes: [themedCss.announcementPubdate], innerHTML: pubdate_formatted })
					]),
					v('div', { classes: [themedCss.announcementBody, themedCss.panelBody], innerHTML: record.body })
				]);
		  })
		: [];

	return v('div', { classes: [themedCss.root] }, [
		v(
			'div',
			{ classes: [themedCss.recordsContainer] },
			record_rows && record_rows.length ? record_rows : [noAnnouncements]
		)
	]);
});
